// src/private/src/pages/app/clients/components/ExportButton.jsx
import React, { memo, useState, useCallback, useMemo } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableChartIcon from '@mui/icons-material/TableChart';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const SKIP_KEYS = new Set(['uuid', 'ingestion_health', 'spendWithFees', 'displayName']);

const COLUMN_LABELS = {
  date: 'Date',
  client__name: 'Client',
  client_id: 'Client ID',
  total_spend_sum: 'Spend',
  total_leads_sum: 'Leads',
  cost_per_lead_avg: 'CPL',
  self_books_sum: 'Self Books',
  accepted_sum: 'Accepted',
  success_sum: 'Success',
  out_of_areas_count_sum: 'Out of Area',
  booked_sum: 'Booked',
  scheduled_sum: 'Scheduled',
  issues_sum: 'Issues',
  demos_sum: 'Demos',
  gross_sales_sum: 'Gross Sold',
  gross_amount_sum: 'Gross Amount',
  net_sales_sum: 'Net Sold',
  net_amount_sum: 'Net Amount',
  vertical: 'Vertical',
  channel__name: 'Channel',
  campaign: 'Campaign',
  campaign_id: 'Campaign ID',
  territory_name: 'Territory',
  client_territory__name: 'Territory',
  state: 'State',
  state_name: 'State',
  dma__name: 'DMA',
  dma_name: 'DMA',
  city: 'City',
  city_name: 'City',
  county: 'County',
  county_name: 'County',
  month: 'Month',
};

// Menu order. `partial` marks server-paginated tables where only the rows
// already loaded in the browser can be exported.
const TABLE_SECTIONS = [
  { key: 'summary', label: 'Summary by Date' },
  { key: 'verticals', label: 'Vertical Performance' },
  { key: 'channels', label: 'Channel Performance' },
  { key: 'campaigns', label: 'Campaign Performance' },
  { key: 'territories', label: 'Territory Performance' },
  { key: 'states', label: 'State Performance' },
  { key: 'dmas', label: 'DMA Performance' },
  { key: 'cities', label: 'City Performance', partial: true },
  { key: 'counties', label: 'County Performance', partial: true },
  { key: 'zips', label: 'ZIP Performance', partial: true },
  { key: 'momData', label: 'Month-over-Month' },
  { key: 'dodData', label: 'Day-over-Day' },
];

const friendlyHeader = (key) => COLUMN_LABELS[key] || key.replace(/_/g, ' ');

const escCsv = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  return str.includes(',') || str.includes('"') || str.includes('\n')
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

// Plain header-row + data CSV — no metadata lines, so Excel/Sheets parse it
// as a clean table. Context (client, table, range) lives in the filename.
const buildTableCsv = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return '';
  const headers = Object.keys(rows[0]).filter((k) => !SKIP_KEYS.has(k));
  return [
    headers.map(friendlyHeader).join(','),
    ...rows.map((row) => headers.map((h) => escCsv(row[h])).join(',')),
  ].join('\n');
};

const buildSection = (title, rows) => {
  const csv = buildTableCsv(rows);
  return csv ? `--- ${title} ---\n${csv}\n` : '';
};

const downloadCsv = (csv, filename) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const slugify = (value) => value.replace(/[^a-z0-9]/gi, '_');

/**
 * Export button with dropdown: one clean CSV per table (with row counts),
 * an all-tables combined CSV, and Print / Save PDF.
 *
 * @param {object} tables - Object with keys for each table's data array:
 *   { summary, verticals, channels, campaigns, territories, states, dmas,
 *     cities, counties, zips, momData, dodData }
 */
function ExportButton({ tables = {}, clientName = 'Client', dateRange = '' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((e) => setAnchorEl(e.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const availableSections = useMemo(
    () =>
      TABLE_SECTIONS.map((section) => ({
        ...section,
        rows: Array.isArray(tables[section.key]) ? tables[section.key] : [],
      })).filter((section) => section.rows.length > 0),
    [tables]
  );
  const hasPartialSections = availableSections.some((section) => section.partial);

  const fileStem = `${slugify(clientName)}_${slugify(dateRange) || 'dashboard'}`;

  const handleExportTable = useCallback(
    (section) => {
      handleClose();
      setExporting(true);
      try {
        const csv = buildTableCsv(section.rows);
        if (csv) {
          downloadCsv(csv, `${fileStem}_${slugify(section.label)}.csv`);
        }
      } finally {
        setExporting(false);
      }
    },
    [fileStem, handleClose]
  );

  const handleExportAll = useCallback(() => {
    handleClose();
    setExporting(true);
    try {
      const sections = availableSections
        .map((section) => buildSection(section.label, section.rows))
        .filter(Boolean);
      if (!sections.length) {
        return;
      }
      const header = `${clientName} Dashboard Export\nDate Range: ${dateRange}\nExported: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })}\n\n`;
      downloadCsv(header + sections.join('\n'), `${fileStem}_all_tables.csv`);
    } finally {
      setExporting(false);
    }
  }, [availableSections, clientName, dateRange, fileStem, handleClose]);

  const handleExportPdf = useCallback(() => {
    handleClose();
    window.print();
  }, [handleClose]);

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
        onClick={handleClick}
        disabled={exporting}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { maxHeight: 480 } } }}
      >
        {availableSections.map((section) => (
          <MenuItem key={section.key} onClick={() => handleExportTable(section)} dense>
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {section.label} ({section.rows.length.toLocaleString()}
              {section.partial ? ' loaded' : ''})
            </ListItemText>
          </MenuItem>
        ))}
        {availableSections.length > 0 && <Divider />}
        <MenuItem onClick={handleExportAll} dense disabled={!availableSections.length}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>All tables (single CSV)</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportPdf} dense>
          <ListItemIcon>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print / Save PDF</ListItemText>
        </MenuItem>
        {hasPartialSections && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 2, py: 0.75, maxWidth: 260 }}
          >
            City / County / ZIP tables export the rows currently loaded on the
            page, not the full dataset.
          </Typography>
        )}
      </Menu>
    </>
  );
}

export default memo(ExportButton);
